import { ArrowLeft, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useQuestions, useStackDetail, type QuestionFilters } from "@/api/queries";
import { QuestionDetail } from "@/components/question-detail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Combobox, FieldLabel, Input } from "@/components/ui/input";
import { Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { Skeleton, SkeletonStat, SkeletonTableRow } from "@/components/ui/skeleton";
import { Stat } from "@/components/ui/stat";
import { cn, formatCount, formatScore } from "@/lib/utils";

const PAGE = 50;
const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase";

const DIFFICULTY_LABELS: Record<number, string> = {
  1: "Level 1 · Foundational",
  2: "Level 2 · Working",
  3: "Level 3 · Proficient",
  4: "Level 4 · Advanced",
  5: "Level 5 · Mastery",
};

export default function StackDetail() {
  const { stackPath = "" } = useParams<{ stackPath: string }>();
  const decodedStack = useMemo(() => {
    try {
      return stackPath.includes("%") ? decodeURIComponent(stackPath) : stackPath;
    } catch {
      return stackPath;
    }
  }, [stackPath]);

  const { data: detail, isLoading: isDetailLoading, error: detailError } = useStackDetail(decodedStack);

  // Question table state
  const [selectedStatusTab, setSelectedStatusTab] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [concept, setConcept] = useState("");
  const [difficulty, setDifficulty] = useState<string>("");
  const [questionType, setQuestionType] = useState<string>("");
  const [offset, setOffset] = useState(0);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);

  // Map status tabs
  const activeStatusFilter = useMemo(() => {
    if (selectedStatusTab === "all") return undefined;
    if (selectedStatusTab === "pending") return ["pending_review", "stale"];
    return [selectedStatusTab];
  }, [selectedStatusTab]);

  const filters: QuestionFilters = {
    stack_path: decodedStack,
    limit: PAGE,
    offset,
    ...(activeStatusFilter ? { status: activeStatusFilter } : {}),
    ...(search ? { search } : {}),
    ...(concept ? { primary_concept: concept } : {}),
    ...(difficulty ? { difficulty_score: parseInt(difficulty, 10) } : {}),
    ...(questionType ? { question_type: questionType } : {}),
  };

  const {
    data: questionsData,
    isLoading: isQuestionsLoading,
    error: questionsError,
    isPlaceholderData,
  } = useQuestions(filters);

  const items = questionsData?.items ?? [];
  const total = questionsData?.total ?? 0;

  const maxDiffCount = useMemo(() => {
    if (!detail?.difficulty_counts) return 1;
    const vals = Object.values(detail.difficulty_counts);
    return Math.max(...vals, 1);
  }, [detail?.difficulty_counts]);

  return (
    <div className="flex flex-col gap-4 p-4 pb-12">
      {/* Top Breadcrumb & Stack Header */}
      <div>
        <Link
          to="/stack-overview"
          className="text-bank-muted hover:text-bank-ink inline-flex items-center gap-1.5 font-mono text-xs transition-colors"
        >
          <ArrowLeft className="size-3.5" /> Back to Stack Overview
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold tracking-tight">{decodedStack}</h1>
            {isDetailLoading ? (
              <Skeleton className="h-5 w-24 rounded" />
            ) : (
              <span className="bg-bank-surface-2 text-bank-muted rounded border border-bank-border px-2 py-0.5 font-mono text-xs">
                bucket: {detail?.bucket ?? "unclassified"}
              </span>
            )}
            {detail?.is_deactivated && (
              <span className="bg-bank-inert-wash text-bank-inert-ink rounded px-2 py-0.5 font-mono text-xs">
                deactivated in taxonomy
              </span>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            {isDetailLoading ? (
              <div className="flex items-center gap-1.5 font-mono text-xs text-bank-muted">
                <Loader2 className="size-3 animate-spin" />
                <span>Loading analytics…</span>
              </div>
            ) : (
              <span className="text-bank-muted font-mono text-xs">
                {formatCount(detail?.total_questions)} total questions
              </span>
            )}
          </div>
        </div>
      </div>

      <ErrorNote error={detailError ?? questionsError} />

      {/* KPI Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {isDetailLoading ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : (
          <>
            <Stat
              label="Total Questions"
              value={formatCount(detail?.total_questions)}
              hint={`${formatCount((detail?.total_questions ?? 0) - (detail?.follow_up_count ?? 0))} primary · ${formatCount(detail?.follow_up_count)} linked follow-ups`}
            />
            <Stat
              label="Approved Pool"
              value={
                detail?.total_questions
                  ? `${formatCount(detail.approved_count)} (${detail.approval_rate}%)`
                  : "0"
              }
              hint="ready for Engine 2 picking"
            />
            <Stat
              label="Duplicate Questions"
              value={
                detail?.total_questions
                  ? `${formatCount(detail.duplicate_count)} (${detail.duplicate_rate}%)`
                  : "0"
              }
              hint="folded into canonical clusters"
            />
            <Stat
              label="Rejected Questions"
              value={
                detail?.total_questions
                  ? `${formatCount(detail.rejected_count)} (${detail.rejection_rate}%)`
                  : "0"
              }
              hint="screened out by reviewer"
            />
            <Stat
              label="Average Quality"
              value={formatScore(detail?.avg_quality_score)}
              hint="evaluated rubric & model answer"
            />
          </>
        )}
      </div>

      {/* Analytics Grid: Concepts & Difficulty */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Concept Coverage */}
        <Panel className="flex flex-col">
          <PanelHeader
            title="Concept Coverage"
            actions={
              isDetailLoading ? (
                <Skeleton className="h-3 w-20" />
              ) : (
                <span className="text-bank-muted font-mono text-xs">
                  {detail?.unique_concepts_count ?? 0} concepts mapped
                </span>
              )
            }
          />
          <div className="max-h-64 flex-1 overflow-auto p-3">
            {isDetailLoading ? (
              <div className="space-y-1.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="border-bank-border flex items-center justify-between rounded-md border px-2.5 py-2"
                  >
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                ))}
              </div>
            ) : !detail?.top_concepts || detail.top_concepts.length === 0 ? (
              <Empty>No concept metadata recorded for this stack.</Empty>
            ) : (
              <div className="space-y-1.5">
                {detail.top_concepts.map((c) => {
                  const isSelected = concept === c.primary_concept;
                  return (
                    <button
                      key={c.primary_concept}
                      onClick={() => {
                        setConcept(isSelected ? "" : c.primary_concept);
                        setOffset(0);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors",
                        isSelected
                          ? "border-bank-accent bg-bank-accent-wash text-bank-accent-ink font-medium"
                          : "border-bank-border hover:bg-bank-surface-2 text-bank-ink",
                      )}
                    >
                      <span className="truncate font-mono" title={c.primary_concept}>
                        {c.primary_concept}
                      </span>
                      <div className="flex shrink-0 items-center gap-2 font-mono text-[11px] tabular-nums">
                        <span className="text-bank-good-ink">{c.approved} appr</span>
                        <span className="text-bank-muted">/ {c.total} total</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Panel>

        {/* Difficulty Distribution & Question Types */}
        <Panel className="flex flex-col">
          <PanelHeader title="Difficulty & Question Types" />
          <div className="space-y-4 p-4">
            {/* Difficulty Spread */}
            <div>
              <p className="text-bank-muted mb-2 font-mono text-[10.5px] tracking-[0.07em] uppercase">
                Difficulty Spread (Levels 1–5)
              </p>
              {isDetailLoading ? (
                <div className="space-y-2.5 py-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="grid grid-cols-[140px_1fr_40px] items-center gap-2 px-2 py-0.5">
                      <Skeleton className="h-3 w-28" />
                      <Skeleton className="h-2 w-full rounded-full" />
                      <Skeleton className="h-3 w-6 ml-auto" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((lvl) => {
                    const count = detail?.difficulty_counts?.[lvl] ?? 0;
                    const pct = (count / maxDiffCount) * 100;
                    const isSelected = difficulty === String(lvl);

                    return (
                      <button
                        key={lvl}
                        onClick={() => {
                          setDifficulty(isSelected ? "" : String(lvl));
                          setOffset(0);
                        }}
                        className={cn(
                          "grid w-full grid-cols-[140px_1fr_40px] items-center gap-2 rounded px-2 py-1 text-left text-xs transition-colors",
                          isSelected ? "bg-bank-surface-2 font-medium" : "hover:bg-bank-surface-2",
                        )}
                      >
                        <span className="text-bank-muted truncate font-mono text-[11px]">
                          {DIFFICULTY_LABELS[lvl]}
                        </span>
                        <div className="bg-bank-surface-3 h-2 w-full overflow-hidden rounded-full">
                          <div
                            className="bg-bank-accent h-full rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-right font-mono text-[11px] tabular-nums">{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Question Types */}
            <div className="border-bank-border border-t pt-3">
              <p className="text-bank-muted mb-2 font-mono text-[10.5px] tracking-[0.07em] uppercase">
                Question Types
              </p>
              {isDetailLoading ? (
                <div className="flex gap-2">
                  <Skeleton className="h-7 w-20 rounded" />
                  <Skeleton className="h-7 w-24 rounded" />
                  <Skeleton className="h-7 w-16 rounded" />
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {Object.entries(detail?.question_type_counts ?? {}).map(([qType, count]) => {
                    const isSelected = questionType === qType;
                    return (
                      <button
                        key={qType}
                        onClick={() => {
                          setQuestionType(isSelected ? "" : qType);
                          setOffset(0);
                        }}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded border px-2.5 py-1 font-mono text-xs transition-colors",
                          isSelected
                            ? "border-bank-accent bg-bank-accent-wash text-bank-accent-ink font-semibold"
                            : "border-bank-border bg-bank-surface-2 hover:bg-bank-surface-3 text-bank-ink",
                        )}
                        title={`Filter by ${qType} question type`}
                      >
                        <span className="capitalize">{qType}</span>
                        <span className="text-bank-muted font-medium tabular-nums">{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Panel>
      </div>

      {/* Question Table Section */}
      <Panel className="flex flex-col">
        <PanelHeader
          title={`Questions in ${decodedStack}`}
          actions={
            isQuestionsLoading ? (
              <div className="flex items-center gap-1.5 text-bank-muted font-mono text-xs">
                <Loader2 className="size-3.5 animate-spin" />
                <span>Loading questions…</span>
              </div>
            ) : (
              <span className="text-bank-muted font-mono text-xs tabular-nums">
                {`${total} matching in bank`}
              </span>
            )
          }
        />

        {/* Status Tabs Bar */}
        <div className="border-bank-border bg-bank-surface-2 flex flex-wrap items-center gap-1 border-b px-3 py-2">
          {[
            { id: "all", label: "All Questions", count: detail?.total_questions ?? 0 },
            { id: "approved", label: "Approved", count: detail?.approved_count ?? 0 },
            {
              id: "pending",
              label: "Needs Review",
              count: (detail?.pending_review_count ?? 0) + (detail?.stale_count ?? 0),
            },
            { id: "duplicate", label: "Duplicates", count: detail?.duplicate_count ?? 0 },
            { id: "rejected", label: "Rejected", count: detail?.rejected_count ?? 0 },
          ].map((tab) => {
            const active = selectedStatusTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setSelectedStatusTab(tab.id);
                  setOffset(0);
                }}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-mono text-xs transition-colors",
                  active
                    ? "bg-bank-surface border-bank-border text-bank-ink font-semibold shadow-xs border"
                    : "text-bank-muted hover:bg-bank-surface-3",
                )}
              >
                {tab.label}
                <span
                  className={cn(
                    "rounded px-1 text-[10px] tabular-nums",
                    active ? "bg-bank-surface-2 text-bank-ink" : "text-bank-muted",
                  )}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filter Controls Bar */}
        <div className="border-bank-border border-b p-3">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_auto]">
            <div>
              <FieldLabel htmlFor="stack-q-search">Search text in {decodedStack}</FieldLabel>
              <Input
                id="stack-q-search"
                value={search}
                placeholder="keywords, concepts, code snippets…"
                onChange={(e) => {
                  setSearch(e.target.value);
                  setOffset(0);
                }}
              />
            </div>

            <div>
              <FieldLabel htmlFor="stack-q-concept">Concept filter</FieldLabel>
              <Combobox
                id="stack-q-concept"
                value={concept}
                onChange={(v) => {
                  setConcept(v);
                  setOffset(0);
                }}
                options={[
                  { value: "", label: "all concepts" },
                  ...(detail?.top_concepts ?? []).map((c) => ({
                    value: c.primary_concept,
                    label: `${c.primary_concept} (${c.total})`,
                  })),
                ]}
              />
            </div>

            <div>
              <FieldLabel htmlFor="stack-q-diff">Difficulty</FieldLabel>
              <Combobox
                id="stack-q-diff"
                value={difficulty}
                onChange={(v) => {
                  setDifficulty(v);
                  setOffset(0);
                }}
                options={[
                  { value: "", label: "any difficulty" },
                  { value: "1", label: "Level 1 (Foundational)" },
                  { value: "2", label: "Level 2 (Working)" },
                  { value: "3", label: "Level 3 (Proficient)" },
                  { value: "4", label: "Level 4 (Advanced)" },
                  { value: "5", label: "Level 5 (Mastery)" },
                ]}
              />
            </div>

            {(search || concept || difficulty || questionType || selectedStatusTab !== "all") && (
              <div className="flex items-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSearch("");
                    setConcept("");
                    setDifficulty("");
                    setQuestionType("");
                    setSelectedStatusTab("all");
                    setOffset(0);
                  }}
                  className="mb-1"
                >
                  Reset filters
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Questions Table */}
        <div className="overflow-x-auto">
          {items.length === 0 && !isQuestionsLoading ? (
            <Empty>No questions found in this stack matching the criteria.</Empty>
          ) : (
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-bank-surface-2 border-bank-border sticky top-0 border-b text-left">
                <tr>
                  <th className={TH}>#</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>Question</th>
                  <th className={TH}>Concept</th>
                  <th className={TH}>Type</th>
                  <th className={cn(TH, "text-right")}>Diff</th>
                  <th className={cn(TH, "text-right")}>Quality</th>
                  <th className={TH}>Cluster</th>
                </tr>
              </thead>
              <tbody className={isPlaceholderData ? "opacity-60" : undefined}>
                {isQuestionsLoading
                  ? Array.from({ length: 8 }).map((_, i) => (
                      <SkeletonTableRow
                        key={i}
                        columns={[
                          "w-8",
                          "w-16",
                          "w-3/4",
                          "w-24",
                          "w-16",
                          "w-8 ml-auto",
                          "w-10 ml-auto",
                          "w-12",
                        ]}
                      />
                    ))
                  : items.map((q) => (
                      <tr
                        key={q.id}
                        onClick={() => setSelectedQuestionId(q.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedQuestionId(q.id);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`View question ${q.id} detail`}
                        className="border-bank-border hover:bg-bank-surface-2 focus-visible:outline-bank-accent cursor-pointer border-t transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2"
                      >
                        <td className="text-bank-muted px-3 py-2 font-mono text-[11px] tabular-nums">
                          {q.id}
                        </td>
                        <td className="px-3 py-2">
                          <Badge value={q.status} />
                        </td>
                        <td className="question-text max-w-xl px-3 py-2 text-[13px]">{q.question_text}</td>
                        <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                          {q.primary_concept ?? "—"}
                        </td>
                        <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                          {q.question_type ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums">
                          {q.difficulty_score ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums">
                          {formatScore(q.quality_score)}
                        </td>
                        <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                          {q.canonical_id
                            ? `→ #${q.canonical_id}`
                            : (q.cluster_size ?? 1) > 1
                              ? "canonical"
                              : "—"}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        <div className="border-bank-border flex items-center justify-between border-t px-3 py-2">
          <p className="text-bank-muted font-mono text-[11px] tabular-nums">
            {total === 0 ? "0" : `${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}
          </p>
          <div className="flex gap-1.5">
            <Button size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              Previous
            </Button>
            <Button size="sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
              Next
            </Button>
          </div>
        </div>
      </Panel>

      {/* Slide-over Question Detail Drawer */}
      <Drawer
        open={selectedQuestionId !== null}
        onClose={() => setSelectedQuestionId(null)}
        title={selectedQuestionId !== null ? `Question #${selectedQuestionId}` : ""}
      >
        {selectedQuestionId !== null && (
          <QuestionDetail id={selectedQuestionId} showStatusActions onOpenQuestion={setSelectedQuestionId} />
        )}
      </Drawer>
    </div>
  );
}
